import { Box, Typography } from '@material-ui/core';
import React, { useRef, useState } from 'react';

import Android12Switch from '../../../../components/Android12Switch';
import Histogram, { HistogramNode } from './components/Histogram';
import RangeSlider from './components/RangeSlider/RangeSlider';

export type Histogram = {
  nodes: HistogramNode[];
  min: number;
  max: number;
};

export type HistogramValue = {
  min: number;
  max: number;
};

export interface RangeSelectionProps {
  title?: string;
  histogram: Histogram;
  defaultValue?: HistogramValue;
  displayOrigin?: boolean;
  toggleOn?: boolean;
  onChange?: ({ min, max }: HistogramValue) => void;
  onToggleChange?: (toggleOn: boolean) => void;
}

const RangeSelection: React.FC<RangeSelectionProps> = ({
  title,
  histogram,
  defaultValue,
  displayOrigin,
  toggleOn,
  onChange,
  onToggleChange
}) => {
  const switchRef = useRef<HTMLInputElement>();
  const switchDefaultChecked = useRef(toggleOn);

  const [highlight, setHighlight] = useState<readonly number[]>(() => {
    if (!defaultValue) {
      return [0, histogram.nodes.length];
    }

    const highlightStart = histogram.nodes.findIndex(
      (node) => node.min === defaultValue.min
    );

    const highlightEnd = histogram.nodes.findIndex(
      (node) => node.max === defaultValue.max
    );

    return [highlightStart, highlightEnd + 1];
  });

  const handleHighlightChange = (values: readonly number[]) => {
    setHighlight(values);

    if (!onChange) return;

    const { min } = histogram.nodes[values[0]];
    const { max } = histogram.nodes[values[1] - 1];

    onChange({ min, max });
  };

  const handleToggleChange = (
    event: React.ChangeEvent<HTMLInputElement> | null,
    checked: boolean
  ) => {
    if (!onToggleChange) return;

    onToggleChange(checked);
  };

  const handleClickInside = () => {
    if (!toggleOn && switchRef.current) {
      switchRef.current.click();
      handleToggleChange(null, true);
    }
  };

  if (!histogram.nodes.length) return null;

  return (
    <Box onClick={handleClickInside}>
      <Box display="flex" alignItems="center">
        {title && <Typography variant="h4">{title}</Typography>}

        <Box flex="1" display="flex" justifyContent="flex-end">
          <Android12Switch
            inputRef={switchRef}
            defaultChecked={switchDefaultChecked.current}
            edge="end"
            color="primary"
            onClick={(event) => event.stopPropagation()}
            onChange={handleToggleChange}
          />
        </Box>
      </Box>

      <Box mt={2} pb={2}>
        <Histogram
          nodes={histogram.nodes}
          highlight={highlight}
          disabled={!toggleOn}
        />

        <RangeSlider
          values={highlight}
          nodes={histogram.nodes}
          mode={2}
          step={1}
          disabled={!toggleOn}
          displayOrigin={displayOrigin}
          domain={[0, histogram.nodes.length]}
          onChange={handleHighlightChange}
          onUpdate={setHighlight}
        />
      </Box>
    </Box>
  );
};

export default RangeSelection;
